import React, { useState, useEffect, useContext } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import ArrowMenu from '../../../../ArrowMenu';
import Table from '../../../../Table';
import CompanyGridCard from './CompanyGridCard';
import TableMenu from '../../../Console/TableMenu';

import AdminService from '../../../../../services/AdminService';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { useUiContext } from '../../../../../context/UiContext';
import tableColsToSortOptions from '../../../../../helpers/tableColsToSortOptions';
import classes from './styles.module.scss';

export default function AllCompanies({
  companies,
  refetch,
  selectedRows,
  setSelectedRows,
  toggleCandidateSelection,
  // sortOptions,
  activeSortOption,
  setActiveSortOption,
  sortOrder,
  setSortOrder,
  setSearchTerm,
  searchHistory,
  searchTerm,
  isFullScreenModeEnabled,
  setIsFullScreenModeEnabled,
  isFullScreenButtonVisible,
  view,
  setView,
  showAddCompanyPopup,
  showEditCompanyPopup,
  currentPage,
  setCurrentPage,
  totalPages,
  filterSwitchOptions,
  activeFilterSwitchOption,
  setActiveFilterSwitchOption,
  candidatesCount,
  isFetching,
  debouncedSearchTerm,
}) {
  const [isArrowMenuVisible, setIsArrowMenuVisible] = useState(false);

  const navigate = useNavigate();

  const { showAddCompanyNoteModal } = useContext(CompanyContext);
  const [currentCompany, setCurrentCompanyState] = useState(null);
  const [rightClickCoords, setRightClickCoords] = useState({ x: 0, y: 0 });
  const [notEnoughSpaceBelow, setNotEnoughSpaceBelow] = useState(false);
  const [activeAction, setActiveAction] = useState('');

  const { t } = useTranslation();

  const {
    showSelectionNeededModal,
    showModal,
    showNotification,
    showUnknownErrorModal,
    setIsFetching,
    showConflictModal,
  } = useUiContext();

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const columns = [
    {
      field: 'isFavorite',
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.favorite'
      ),
      type: 'companyBooleanFavorite',
      sortable: true,
    },
    {
      field: 'company.id',
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.id'
      ),
      sortable: true,
    },
    {
      field: 'company.name',
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.name'
      ),
      sortable: true,
    },
    {
      field: 'company.industry.name',
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.industry'
      ),
      sortable: true,
    },
    {
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.country'
      ),
      field: 'company.country',
      sortable: true,
    },
    {
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.contact'
      ),
      field: 'company.primaryManager',
      type: 'primaryManager',
      sortField: 'company.primaryManager.lastName',
      sortable: true,
    },
    {
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.activeJobsCount'
      ),
      field: 'activeJobsCount',
      sortable: true,
      type: 'companyCount',
    },
    {
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.activeCandidatesCount'
      ),
      field: 'activeCandidatesCount',
      sortable: true,
      type: 'companyCount',
    },
    {
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.hiresCount'
      ),
      field: 'hiresCount',
      sortable: true,
      type: 'companyCount',
    },
    {
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.sla'
      ),
      field: 'sla',
      sortable: true,
    },
    {
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.lastActivity'
      ),
      field: 'lastActivity',
      sortable: true,
    },
    {
      name: t(
        'dashboardComponents.Companies.CompaniesList.AllCompanies.companyTable.columns.status'
      ),
      field: 'company.status',
      sortable: true,
      type: 'companyStatus',
    },
  ];

  const sortOptions = tableColsToSortOptions(columns);

  useEffect(() => {
    setActiveSortOption({
      value: 'createdAt',
      label: t(
        'dashboardComponents.Candidates.CandidatesList.AllCandidates.candidateTable.columns.createdOn'
      ),
    });
  }, [setActiveSortOption, t]);

  const showCompanyInfo = (id) => {
    navigate(`/tools/companies/company-details/${id}`);
  };

  const contextMenuOptions = [
    {
      label: currentCompany?.notes?.length
        ? t(
            'dashboardComponents.Candidates.CandidatesList.AllCandidates.candidateTable.contextMenu.editNote'
          )
        : t(
            'dashboardComponents.Candidates.CandidatesList.AllCandidates.candidateTable.contextMenu.addNote'
          ),
      onClick: () => showAddCompanyNoteModal(currentCompany, refetch),
    },
    {
      label: t('common.postJob'),
      onClick: () =>
        navigate(`/tools/jobs/post-job?sourceCompanyId=${currentCompany.id}`),
    },
  ];

  const actions = [
    {
      label: t('common.add'),
      value: 'Add',
      onClick: showAddCompanyPopup,
    },
    {
      label: t('common.edit'),
      value: 'Edit',
      onClick: () => {
        if (selectedRows.length) {
          showEditCompanyPopup(true);
        } else {
          showSelectionNeededModal();
        }
      },
    },
    {
      label: t('common.delete'),
      value: 'Delete',
      onClick: () => {
        if (selectedRows.length) {
          showModal({
            icon: 'delete',
            title: t(
              'dashboardComponents.Console.CompanyManagement.view.deleteConfirmationTitle'
            ),
            text: t(
              'dashboardComponents.Console.CompanyManagement.view.deleteConfirmationText'
            ),
            onConfirm: async () => {
              try {
                setIsFetching(true);
                await Promise.all(
                  selectedRows.map((row) =>
                    AdminService.deleteCompany({ id: row })
                  )
                );
                showNotification({
                  text: t('Company deleted successfully'),
                });
                refetch();
              } catch (error) {
                console.log(error);
                if (error.response.status === 409) {
                  showConflictModal(error.response.data.message);
                } else {
                  showUnknownErrorModal();
                }
              } finally {
                setIsFetching(false);
              }
            },
            onCancel: () => {},
            dismissButtonLabel: t('common.no'),
            confirmButtonLabel: t('common.yes'),
          });
        } else {
          showSelectionNeededModal();
        }
      },
    },
  ];

  const showTableContextMenu = (id, clientX, clientY) => {
    const menuHeight = 131;
    const notEnoughSpace = window.innerHeight - clientY < menuHeight;
    setNotEnoughSpaceBelow(notEnoughSpace);

    setRightClickCoords({
      x: clientX,
      y: notEnoughSpace ? clientY - 131 - 30 : clientY,
    });
    const company = companies.find((cnd) => cnd.id === id);
    setCurrentCompanyState(company);
    setIsArrowMenuVisible(true);
  };

  let tableContent;
  let gridContent;

  let tableHeight = 'auto';

  if (isTabletOrMobile) {
    tableHeight = 'auto';
  }

  tableContent = (
    <div className={classes.table}>
      <Table
        style={{
          overflowX: isTabletOrMobile ? 'unset' : '',
        }}
        noSort
        height={tableHeight}
        name="Candidates"
        onRowClick={showCompanyInfo}
        onRowRightClick={showTableContextMenu}
        rows={companies}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sortOptions={sortOptions}
        setActiveSortOption={setActiveSortOption}
        activeSortOption={activeSortOption}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        columns={columns}
        refetch={refetch}
        isFetching={isFetching}
      />
    </div>
  );

  gridContent = (
    <div className={classes.gridContainer}>
      <div
        className={classes.grid}
        style={{
          overflowX: isTabletOrMobile ? 'unset' : '',
        }}
      >
        {companies?.map((company) => {
          return (
            <CompanyGridCard
              selectedRows={selectedRows}
              key={company.id}
              company={company}
              refetch={refetch}
              toggleCandidateSelection={toggleCandidateSelection}
              showCompanyInfo={showCompanyInfo}
              showEditCompanyPopup={showEditCompanyPopup}
            />
          );
        })}
      </div>
    </div>
  );

  if (!companies?.length && !isFetching) {
    const noData = (
      <div className={classes.noContent}>
        <p>
          {t(
            'dashboardComponents.Companies.CompaniesList.AllCompanies.allCompanies.noContent.message1'
          )}
        </p>
        <p>
          {t(
            'dashboardComponents.Companies.CompaniesList.AllCompanies.allCompanies.noContent.message2'
          )}
        </p>
      </div>
    );

    tableContent = <div style={{ height: tableHeight }}>{noData}</div>;
    gridContent = <div style={{ height: tableHeight }}>{noData}</div>;
  }

  if (isFetching && debouncedSearchTerm && currentPage === 1) {
    const noData = (
      <div className={classes.noContent}>
        <p>{t('common.searchingMessage')}</p>
      </div>
    );

    tableContent = <div style={{ height: tableHeight }}>{noData}</div>;
    gridContent = <div style={{ height: tableHeight }}>{noData}</div>;
  }

  if (!companies?.length && !isFetching && debouncedSearchTerm) {
    const noData = (
      <div className={classes.noContent}>
        <p>{t('common.noResultsFoundMessage')}</p>
      </div>
    );

    tableContent = <div style={{ height: tableHeight }}>{noData}</div>;
    gridContent = <div style={{ height: tableHeight }}>{noData}</div>;
  }

  return (
    <div className={classes.AllCompanies}>
      {isArrowMenuVisible && (
        <div
          className={classes.arrowMenuContainer}
          style={{ top: rightClickCoords.y, left: rightClickCoords.x }}
        >
          <ArrowMenu
            options={contextMenuOptions}
            isVisible
            setIsVisible={setIsArrowMenuVisible}
            bottomArrow={notEnoughSpaceBelow}
          />
        </div>
      )}
      <div className={classes.viewContainer}>
        <div
          className={classNames(classes.tableMenuContainer, {
            [classes.withoutButton]: !isFullScreenButtonVisible,
          })}
        >
          <div className={classes.innerContainer}>
            <TableMenu
              filterSwitchOptions={filterSwitchOptions}
              activeFilterSwitchOption={activeFilterSwitchOption}
              setActiveFilterSwitchOption={setActiveFilterSwitchOption}
              setSortOrder={setSortOrder}
              actions={actions}
              activeAction={activeAction}
              setActiveAction={setActiveAction}
              dangerAction={t('common.delete')}
              sortOptions={sortOptions}
              activeSortOption={activeSortOption}
              setActiveSortOption={setActiveSortOption}
              selectedCount={selectedRows.length}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchHistory={searchHistory}
              view={view}
              setView={setView}
              desktopLayoutOnMobile={false}
              withFullScreenButton={isFullScreenButtonVisible}
              isFullScreenModeEnabled={isFullScreenModeEnabled}
              setIsFullScreenModeEnabled={setIsFullScreenModeEnabled}
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              searchResultCount={
                debouncedSearchTerm && !isFetching ? candidatesCount : null
              }
            />
          </div>
        </div>
        {view === 'list' ? tableContent : gridContent}
      </div>
    </div>
  );
}
